import { useId, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Dialog } from './Dialog';
import { cn } from '@/lib/cn';

/**
 * Help on demand: screens carry labels, values and controls only. An explanation sits behind this small icon —
 * shown beside it on hover or keyboard focus, and in a small dialog (with its own close button) on click or tap,
 * which is the only way to reach it on a phone. `title` names the thing explained; `children` is the explanation.
 *
 * The mark is 20 px; its touch target is 44 px. The extra 12 px on each side is an absolutely placed element behind
 * the button (a press on it is a press on the button), so it takes no room, nothing beside the icon moves, and the
 * button's own box stays 20 px. Where another control sits closer than 12 px on one side, pass `INFO_TARGET_RIGHT`
 * or `INFO_TARGET_LEFT` in `className` to shift the target off it.
 */
/** The 44 px target reaches right: for an icon with a control close on its left. */
export const INFO_TARGET_RIGHT = '[&_[data-info-target]]:-left-0.5 [&_[data-info-target]]:-right-[22px]';
/** The 44 px target reaches left: for an icon with a control close on its right. */
export const INFO_TARGET_LEFT = '[&_[data-info-target]]:-left-[22px] [&_[data-info-target]]:-right-0.5';

export function InfoTip({ title, children, className, align = 'start', side = 'bottom', more, size = 'sm' }: {
  title: string; children: ReactNode; className?: string; align?: 'start' | 'end';
  /** A longer text for the dialog (a step's whole guide); the hover text stays `children`. */
  more?: ReactNode; size?: 'sm' | 'md';
  /** Where the hover text opens. `top` is for an icon near the bottom of the screen (a notification). */
  side?: 'bottom' | 'top';
}) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const button = useRef<HTMLButtonElement>(null);
  return (
    <span className={cn('group/info relative inline-flex align-middle', className)} data-info-tip>
      <span className="relative inline-flex">
        <span aria-hidden data-info-target className="absolute -inset-3" onClick={() => { button.current?.focus(); setOpen(true); }} />
        <button
          ref={button}
          type="button"
          className="relative inline-flex size-5 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand)]"
          aria-label={`About: ${title}`}
          aria-describedby={open ? undefined : tipId}
          onClick={() => setOpen(true)}
        >
          <Info className="size-4" aria-hidden />
        </button>
      </span>
      <span
        id={tipId}
        role="tooltip"
        className={cn(
          'pointer-events-none absolute z-popover hidden w-64 max-w-[min(16rem,calc(100vw-2rem))] rounded-control bg-surface p-2.5 text-left text-[12px] font-normal normal-case leading-snug tracking-normal text-ink-2 shadow-lg ring-1 ring-line-strong',
          'group-hover/info:block group-focus-within/info:block',
          side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
          align === 'end' ? 'right-0' : 'left-0',
          open && '!hidden',
        )}
      >
        {children}
      </span>
      <Dialog open={open} onClose={() => setOpen(false)} title={title} size={size}>
        <div className="space-y-2 text-sm text-ink-2" data-info-body>{more ?? children}</div>
      </Dialog>
    </span>
  );
}
